import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { clearStoredToken, getStoredToken, setStoredToken } from '../../MicrospecDynamicUI/Helpers/LocalPersistenceUtils';
import { BusinessProduct } from '../../MicrospecDynamicUI/Models/Business/BusinessProduct';
import { BusinessSpecification } from '../../MicrospecDynamicUI/Models/Business/BusinessSpecification';
import { specificationMappers } from '../Utils/specificationMappers';

export interface CartItem {
    /** Unique id for this cart entry (allows multiple entries of the same product). */
    cartItemId: string;
    product: BusinessProduct;
    quantity: number;
    /** Specifications with user-selected values stored in type_characteristics. */
    specifications: BusinessSpecification[];
}

interface CartContextType {
    items: CartItem[];
    addItem: (product: BusinessProduct, quantity: number, specifications: BusinessSpecification[]) => void;
    removeItem: (cartItemId: string) => void;
    updateQuantity: (cartItemId: string, quantity: number) => void;
    clearCart: () => void;
    totalItems: number;
    /** Sum of base product prices (price × quantity) */
    totalBasePrice: number;
    /** Sum of specification add-on prices */
    totalAdditionsPrice: number;
    totalPrice: number;
}

const CartContext = createContext<CartContextType | null>(null);

let _idCounter = 0;
function nextId(): string {
    return `cart-${Date.now()}-${++_idCounter}`;
}

interface CartProviderProps {
    children: ReactNode;
    config?: any;
}

const STORAGE_KEY = 'golan_cart_v1';
const STORAGE_TYPE = 'localStorage';
const CART_ENC_KEY = 'gtcs0l@n-t1mb3r-c4rt-s3cur3-2026';

const CartProvider = ({ children }: CartProviderProps) => {
    const [items, setItems] = useState<CartItem[]>([]);
    // Tracks whether the initial async load from storage has completed.
    // Prevents writing an empty array back before the load finishes.
    const loadedRef = useRef(false);

    // Load on mount
    useEffect(() => {
        (async () => {
            try {
                const raw = await getStoredToken(STORAGE_TYPE, STORAGE_KEY, CART_ENC_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw) as CartItem[];
                    if (Array.isArray(parsed)) setItems(parsed);
                }
            } catch {
                // Corrupted or missing — start with empty cart
            } finally {
                loadedRef.current = true;
            }
        })();
    }, []);

    // Persist on every change (skip until initial load is done)
    useEffect(() => {
        if (!loadedRef.current) return;
        (async () => {
            try {
                if (items.length === 0) {
                    clearStoredToken(STORAGE_TYPE, STORAGE_KEY);
                } else {
                    await setStoredToken(STORAGE_TYPE, STORAGE_KEY, JSON.stringify(items), null, CART_ENC_KEY);
                }
            } catch {
                // Storage unavailable or quota exceeded — silently ignore
            }
        })();
    }, [items]);

    const addItem = useCallback(
        (product: BusinessProduct, quantity: number, specifications: BusinessSpecification[]) => {
            setItems(prev => [
                ...prev,
                {
                    cartItemId: nextId(),
                    product,
                    quantity,
                    specifications,
                },
            ]);
        },
        [],
    );

    const removeItem = useCallback((cartItemId: string) => {
        setItems(prev => prev.filter(i => i.cartItemId !== cartItemId));
    }, []);

    const updateQuantity = useCallback((cartItemId: string, quantity: number) => {
        setItems(prev =>
            prev.map(i => (i.cartItemId === cartItemId ? { ...i, quantity: Math.max(1, quantity) } : i)),
        );
    }, []);

    const clearCart = useCallback(() => setItems([]), []);

    const totalItems = items.length;

    const totalBasePrice = useMemo(
        () => items.reduce((sum, item) => sum + (item.product.price ?? 0) * item.quantity, 0),
        [items],
    );

    const totalAdditionsPrice = useMemo(
        () =>
            items.reduce((sum, item) => {
                const addons = item.specifications.reduce((specSum, spec) => {
                    const mapper = specificationMappers[spec.type];
                    return specSum + (mapper ? mapper.additionalPriceMapper(spec, item.quantity) : 0);
                }, 0);
                return sum + addons;
            }, 0),
        [items],
    );

    const totalPrice = totalBasePrice + totalAdditionsPrice;

    return (
        <CartContext.Provider
            value={{
                items,
                addItem,
                removeItem,
                updateQuantity,
                clearCart,
                totalItems,
                totalBasePrice,
                totalAdditionsPrice,
                totalPrice,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = (): CartContextType => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used within CartProvider');
    return ctx;
};

export default CartProvider;
